<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RlTimezoneSelect from '@/components/common/RlTimezoneSelect.vue'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import DowntimeDialog from '@/components/eos/downtime/DowntimeDialog.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useDowntimes } from '@/composables/eos/downtime'
import { NotifyFail } from '@/helpers/core/notifications'
import { rangeToISOInTimezone } from '@/util/datetime'
import { getSiteDowntimeTypes } from '@/services/eos'
import { getDowntimeDetails } from '@/services/eos/downtimes'
import { SiteType } from '@/types/core/Site'
import type { IDowntime, IGroupedDowntime } from '@/types/eos/DowntimeEvent'
import type { IDowntimeFilters } from '@/types/eos/filters'
import type SiteDowntimeOptions from '@/types/eos/SiteDowntimeOptions'
import type SiteDowntimeType from '@/types/eos/SiteDowntimeType'
import type { ISiteMapRack, ISiteMapStructure } from '@/types/eos/SiteMap'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import type { CheckboxValueType } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, onMounted, reactive, ref, watch } from 'vue'

const filters = reactive<IDowntimeFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[1],
  site_id: undefined,
  structure_id: undefined,
  rack_id: undefined,
  downtime_type_ids: undefined,
  from_datetime: undefined,
  to_datetime: undefined,
  dispatch_id: undefined,
})
const dateRangeFilter = ref<[Date, Date] | undefined>()
const timezoneFilter = ref(DateTime.now().zoneName)
const downtimeCreationDialogVisible = ref(false)
const moreFiltersVisible = ref(false)
const selectedStructure = ref<ISiteMapStructure>()
const selectedRack = ref<ISiteMapRack>()

// Add check all functionality variables
const checkAllDowntimeTypes = ref(false)
const indeterminateDowntimeTypes = ref(false)
const downtimeOptions = ref<SiteDowntimeOptions[]>([])
const selectedDowntimeTypes = ref<number[]>([])

// Individual filters for each expanded section
const expandedFilters = ref<Record<string, { structureName: string; rackName: string; minerTags: string[] }>>({})

const { getGroupedDowntimesQuery, bulkDeleteDowntimesMutation, removeMinersFromDowntimeMutation } = useDowntimes()

const { isFetching, isError, isRefetching, isPending, data, error, refetch } = getGroupedDowntimesQuery(filters)

// Function to get filtered data for a specific expanded section
const getFilteredDataForSection = (key: string) => {
  const data = expandedData.value[key]
  if (!data) return []

  const filters = expandedFilters.value[key] || { structureName: '', rackName: '', minerTags: [] }

  return data.filter((row: any) => {
    // Filter by structure name
    if (filters.structureName && !row.structure?.name?.toLowerCase().includes(filters.structureName.toLowerCase())) {
      return false
    }

    // Filter by rack name
    if (filters.rackName && !row.rack?.name?.toLowerCase().includes(filters.rackName.toLowerCase())) {
      return false
    }

    // Filter by miner tags
    if (filters.minerTags && filters.minerTags.length > 0) {
      const hasMatchingTag = row.affected_miners?.some((miner: any) =>
        filters.minerTags.some((tag) =>
          miner.tags?.some((minerTag: string) => minerTag.toLowerCase().includes(tag.toLowerCase()))
        )
      )
      if (!hasMatchingTag) {
        return false
      }
    }

    return true
  })
}

// Function to get filtered affected miners for a specific downtime row
const getFilteredAffectedMiners = (key: string, downtimeRow: any) => {
  const filters = expandedFilters.value[key] || { structureName: '', rackName: '', minerTags: [] }

  if (!downtimeRow.affected_miners) {
    return []
  }

  // If no miner tags filter is applied, return all miners
  if (!filters.minerTags || filters.minerTags.length === 0) {
    return downtimeRow.affected_miners
  }

  // Filter miners based on selected tags
  return downtimeRow.affected_miners.filter((miner: any) => {
    return filters.minerTags.some((tag) =>
      miner.tags?.some((minerTag: string) => minerTag.toLowerCase().includes(tag.toLowerCase()))
    )
  })
}

// Function to initialize filters for a section
const initializeFiltersForSection = (key: string) => {
  if (!expandedFilters.value[key]) {
    expandedFilters.value[key] = { structureName: '', rackName: '', minerTags: [] }
  }
}

// Function to get filter value for a section
const getFilterValue = (key: string, field: 'structureName' | 'rackName') => {
  return expandedFilters.value[key]?.[field] || ''
}

// Function to get miner tags filter value for a section
const getMinerTagsFilterValue = (key: string) => {
  return expandedFilters.value[key]?.minerTags || []
}

// Function to set filter value for a section
const setFilterValue = (key: string, field: 'structureName' | 'rackName', value: string) => {
  initializeFiltersForSection(key)
  expandedFilters.value[key][field] = value
}

// Function to set miner tags filter value for a section
const setMinerTagsFilterValue = (key: string, value: string[]) => {
  initializeFiltersForSection(key)
  expandedFilters.value[key].minerTags = value
}

// Function to get all unique miner tags from expanded data
const getAllUniqueMinerTags = () => {
  const allTags = new Set<string>()

  Object.values(expandedData.value).forEach((sectionData: any[]) => {
    sectionData.forEach((row: any) => {
      if (row.affected_miners) {
        row.affected_miners.forEach((miner: any) => {
          if (miner.tags && Array.isArray(miner.tags)) {
            miner.tags.forEach((tag: string) => {
              if (tag && tag.trim()) {
                allTags.add(tag)
              }
            })
          }
        })
      }
    })
  })

  return Array.from(allTags).sort()
}

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id
}

const onStructureFilterUpdate = (value?: ISiteMapStructure) => {
  filters.structure_id = value?.id
  selectedStructure.value = value
  // Sync to all expanded row filters
  const structureName = value?.name ?? ''
  Object.keys(expandedFilters.value).forEach((key) => {
    initializeFiltersForSection(key)
    expandedFilters.value[key].structureName = structureName
  })
}

const onRackFilterUpdate = (value?: ISiteMapRack) => {
  filters.rack_id = value?.id
  selectedRack.value = value
  // Sync to all expanded row filters
  const rackName = value?.name ?? ''
  Object.keys(expandedFilters.value).forEach((key) => {
    initializeFiltersForSection(key)
    expandedFilters.value[key].rackName = rackName
  })
}

const dispatchIdSearchSettings = {
  key: 'dispatch_id',
  value: 'Search',
  type: 'search',
  placeholder: 'Dispatch ID',
  style: 'width: 272px',
  onChange: (val: string) => {
    filters.dispatch_id = val === '' ? undefined : val
  },
}

const onDowntimeTypeChange = (value: number[]) => {
  filters.downtime_type_ids = value.join(',')
}

// Add watcher for check all functionality
watch(selectedDowntimeTypes, (val) => {
  const allOptions = downtimeOptions.value.flatMap((group) => group.options.map((option) => option.key))
  if (val.length === 0) {
    checkAllDowntimeTypes.value = false
    indeterminateDowntimeTypes.value = false
  } else if (val.length === allOptions.length) {
    checkAllDowntimeTypes.value = true
    indeterminateDowntimeTypes.value = false
  } else {
    checkAllDowntimeTypes.value = false
    indeterminateDowntimeTypes.value = true
  }
})

// Add check all handler
const handleCheckAllDowntimeTypes = (val: CheckboxValueType) => {
  indeterminateDowntimeTypes.value = false
  if (val) {
    const allOptions = downtimeOptions.value.flatMap((group) => group.options.map((option) => option.key))
    selectedDowntimeTypes.value = allOptions
  } else {
    selectedDowntimeTypes.value = []
  }
  onDowntimeTypeChange(selectedDowntimeTypes.value)
}

const dateRangeISO = computed(() => {
  const range = dateRangeFilter.value
  if (Array.isArray(range) && range[0] instanceof Date && range[1] instanceof Date) {
    return rangeToISOInTimezone([range[0], range[1]], timezoneFilter.value)
  }
  return null
})

watch(dateRangeISO, (iso) => {
  filters.from_datetime = iso?.startISO
  filters.to_datetime = iso?.endISO
})

const tableSelection = ref<IDowntime[]>([])
const selectedMiners = ref<any[]>([])
const selectedDowntime = ref<IDowntime>()

const handleTableSelect = (updatedSelection: IDowntime[]) => {
  tableSelection.value = updatedSelection
}

const handleMinerSelect = (updatedSelection: any[], row: IDowntime) => {
  selectedMiners.value = updatedSelection
  selectedDowntime.value = row
}

async function generateGroupedDowntimeTypes() {
  const downtimeTypesData = await getSiteDowntimeTypes()

  // @ts-expect-error - Object.groupBy is not typed
  const downtimesByCategory: Partial<Record<string, SiteDowntimeType[]>> = Object.groupBy(
    downtimeTypesData,
    (dt: SiteDowntimeType) => dt.type_category
  )

  for (const downtimeCategory in downtimesByCategory) {
    downtimeOptions.value.push({
      label: downtimeCategory,
      options: downtimesByCategory[downtimeCategory]!.map((dt: SiteDowntimeType) => {
        return {
          display: `${dt.type_category} - ${dt.type_name}`,
          key: dt.id,
          value: dt,
        }
      }),
    })
  }
}

onMounted(async () => {
  await generateGroupedDowntimeTypes()
})

const expandedData = ref<Record<string, any[]>>({})
const expandedLoading = ref<Record<string, boolean>>({})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleTableExpand = async (row: IGroupedDowntime, _: IGroupedDowntime[] | boolean) => {
  const key = `${row.site_id}-${row.for_hour_utc}-${row.downtime_type_id}`
  if (!expandedData.value[key]) {
    expandedLoading.value[key] = true
    try {
      const response = await getDowntimeDetails(row.site_id, row.for_hour_utc, row.downtime_type_id)
      expandedData.value[key] = response
      // Initialize expanded filters with current main filter values if they exist
      initializeFiltersForSection(key)
      if (selectedStructure.value?.name) {
        expandedFilters.value[key].structureName = selectedStructure.value.name
      }
      if (selectedRack.value?.name) {
        expandedFilters.value[key].rackName = selectedRack.value.name
      }
    } catch {
      NotifyFail('Failed to fetch downtime details')
    } finally {
      expandedLoading.value[key] = false
    }
  }
}

const groupedTableRef = ref()

const closeAllExpandedRows = () => {
  if (groupedTableRef.value) {
    data.value?.data.forEach((row) => {
      groupedTableRef.value.toggleRowExpansion(row, false)
    })
  }
}

const cleanTableExpandData = () => {
  tableSelection.value = []
  expandedData.value = {}
  expandedLoading.value = {}
  closeAllExpandedRows()
}

const hourEndingFromHour = (hour: string) => {
  const dateTime = DateTime.fromISO(hour).setZone(timezoneFilter.value)
  if (!dateTime.isValid) {
    return '-'
  }

  return dateTime.hour + 1
}

/** Formats a UTC ISO datetime for table display in the selected timezone. */
const formatHourInSelectedTz = (_row: unknown, _column: unknown, cellValue: string) => {
  const dt = DateTime.fromISO(cellValue).setZone(timezoneFilter.value)
  return dt.isValid ? dt.toFormat('MM/dd/yyyy HH:mm:ss ZZZZ') : '-'
}

const escapeHtml = (str: string) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
</script>

<template>
  <ViewLayout title="Downtimes Events">
    <template #controls>
      <el-button
        v-if="tableSelection.length > 0"
        type="danger"
        @click="
          DeleteConfirmationModal(
            'Delete Downtimes',
            `Are you sure to delete ${tableSelection.length} downtime events? This action cannot be undone.`,
            tableSelection.map((evt: IDowntime) => evt.id),
            bulkDeleteDowntimesMutation.mutateAsync,
            cleanTableExpandData
          )
        "
        >Delete Downtimes</el-button
      >
      <el-button
        v-if="selectedMiners.length > 0 && selectedDowntime"
        type="warning"
        @click="
          DeleteConfirmationModal(
            'Remove Miners',
            `Are you sure to remove ${selectedMiners.length} miners affecting rack ${escapeHtml(selectedDowntime?.rack.name ?? '')} from
            downtime at ${escapeHtml(selectedDowntime?.site.name || 'unknown site')} at hour
            ${DateTime.fromISO(selectedDowntime!.for_hour_utc).setZone(timezoneFilter).toFormat('MM/dd/yyyy HH:mm:ss ZZZZ')}?
            This action cannot be undone.`,
            selectedMiners.map((miner) => miner.id),
            () =>
              removeMinersFromDowntimeMutation.mutateAsync({
                downtimeID: selectedDowntime!.id,
                minerIDs: selectedMiners.map((miner) => miner.id),
              }),
            () => {
              cleanTableExpandData()
              selectedMiners = []
              selectedDowntime = undefined
            }
          )
        "
        >Remove Miners</el-button
      >
      <el-button type="primary" @click="downtimeCreationDialogVisible = true">Create Downtime</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex flex-wrap gap-2 items-center">
        <!-- Primary filters - always visible -->
        <div class="min-w-[200px] flex-1 md:flex-none">
          <RcSiteSelect
            placeholder="Site ID or Name"
            :type="SiteType.DataCenter"
            :disabled="isPending"
            style="width: 100%"
            @update:model-value="onSiteFilterUpdate"
          />
        </div>
        <div class="min-w-[200px] flex-1 md:flex-none">
          <el-select
            v-model="selectedDowntimeTypes"
            multiple
            :disabled="isPending"
            style="width: 100%"
            placeholder="Downtime Type"
            filterable
            clearable
            collapse-tags
            collapse-tags-tooltip
            :max-collapse-tags="0"
            popper-class="custom-header"
            @change="onDowntimeTypeChange"
          >
            <template #header>
              <el-checkbox
                v-model="checkAllDowntimeTypes"
                :indeterminate="indeterminateDowntimeTypes"
                class="flex h-auto"
                @change="handleCheckAllDowntimeTypes"
              >
                All
              </el-checkbox>
            </template>
            <el-option-group v-for="group in downtimeOptions" :key="group.label" :label="group.label">
              <el-option v-for="item in group.options" :key="item.key" :label="item.display" :value="item.key" />
            </el-option-group>
          </el-select>
        </div>
        <div class="flex items-center gap-2 min-w-[200px] flex-1 md:flex-none flex-nowrap">
          <el-date-picker
            v-model="dateRangeFilter"
            :disabled="isPending"
            type="datetimerange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="YYYY-MM-DD HH:mm:ss"
            date-format="YYYY/MM/DD ddd"
            time-format="A hh:mm:ss"
            editable
            clearable
            class="w-full"
          />
          <RlTimezoneSelect class="w-56" v-model="timezoneFilter" />
        </div>
        <!-- More Filters Popover -->
        <el-popover
          placement="bottom-start"
          width="400"
          trigger="click"
          persistent
          :visible="moreFiltersVisible"
          @before-leave="() => false"
        >
          <template #reference>
            <el-button @click="moreFiltersVisible = !moreFiltersVisible">More Filters</el-button>
          </template>
          <div class="flex flex-col gap-4 min-w-[260px]">
            <div class="flex items-center">
              <span class="inline-block min-w-[70px] text-right mr-2 text-xs text-text-tertiary">Structure</span>
              <EosSiteMapStructureSelect
                placeholder="Structure"
                :disabled="isPending"
                style="width: 272px"
                :site-id="filters.site_id"
                @update:model-value="onStructureFilterUpdate"
              />
            </div>
            <div class="flex items-center">
              <span class="inline-block min-w-[70px] text-right mr-2 text-xs text-text-tertiary">Rack</span>
              <EosSiteMapRackSelect
                placeholder="Rack"
                :disabled="isPending"
                style="width: 272px"
                :site-id="filters.site_id"
                :structure-id="filters.structure_id"
                @update:model-value="onRackFilterUpdate"
              />
            </div>
            <div class="flex items-center">
              <span class="inline-block min-w-[70px] text-right mr-2 text-xs text-text-tertiary">Dispatch ID</span>
              <TableSearchFilter :search-settings="dispatchIdSearchSettings" :disabled="isPending" />
            </div>
            <div class="flex justify-center mt-2">
              <el-button type="primary" size="small" @click="moreFiltersVisible = false">Close</el-button>
            </div>
          </div>
        </el-popover>
      </div>

      <el-table
        ref="groupedTableRef"
        v-loading="isFetching"
        :data="data?.data"
        empty-text="No data"
        stripe
        @expand-change="handleTableExpand"
      >
        <el-table-column type="expand">
          <template #default="scope">
            <div class="mb-4">
              <div class="flex gap-2 mb-2 justify-end">
                <el-input
                  :model-value="
                    getFilterValue(
                      `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`,
                      'structureName'
                    )
                  "
                  placeholder="Structure"
                  :disabled="isPending"
                  style="width: 240px"
                  clearable
                  @update:model-value="
                    (value: string) =>
                      setFilterValue(
                        `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`,
                        'structureName',
                        value
                      )
                  "
                />
                <el-input
                  :model-value="
                    getFilterValue(
                      `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`,
                      'rackName'
                    )
                  "
                  placeholder="Rack"
                  :disabled="isPending"
                  style="width: 240px"
                  clearable
                  @update:model-value="
                    (value: string) =>
                      setFilterValue(
                        `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`,
                        'rackName',
                        value
                      )
                  "
                />
                <el-select
                  :model-value="
                    getMinerTagsFilterValue(
                      `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`
                    )
                  "
                  placeholder="Miner Tags"
                  :disabled="isPending"
                  style="width: 240px"
                  multiple
                  filterable
                  allow-create
                  default-first-option
                  clearable
                  collapse-tags
                  collapse-tags-tooltip
                  :max-collapse-tags="1"
                  @update:model-value="
                    (value: string[]) =>
                      setMinerTagsFilterValue(
                        `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`,
                        value
                      )
                  "
                >
                  <el-option v-for="tag in getAllUniqueMinerTags()" :key="tag" :label="tag" :value="tag" />
                </el-select>
              </div>
            </div>
            <el-table
              v-loading="
                expandedLoading[`${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`]
              "
              :data="
                getFilteredDataForSection(
                  `${scope.row.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type_id}`
                )
              "
              empty-text="No data"
              stripe
              size="small"
              @selection-change="handleTableSelect"
            >
              <el-table-column label="Downtimes" header-align="center">
                <el-table-column type="selection" width="55" />
                <el-table-column type="expand">
                  <template #default="scope">
                    <el-table
                      :data="
                        getFilteredAffectedMiners(
                          `${scope.row.site.site_id}-${scope.row.for_hour_utc}-${scope.row.downtime_type.id}`,
                          scope.row
                        )
                      "
                      empty-text="No data"
                      stripe
                      size="small"
                      @selection-change="(selection: any) => handleMinerSelect(selection, scope.row as IDowntime)"
                    >
                      <el-table-column label="Affected Miners" header-align="center">
                        <el-table-column type="selection" width="55" />
                        <el-table-column prop="name" label="Name" />
                        <el-table-column prop="model" label="Model" />
                        <el-table-column prop="ip" label="IP Address" />
                        <el-table-column prop="tags" label="Tags">
                          <template #default="scope">
                            <div class="flex flex-wrap gap-2">
                              <el-tag v-for="tag in scope.row.tags" :key="tag">{{ tag }}</el-tag>
                            </div>
                          </template>
                        </el-table-column>
                        <el-table-column prop="hosting_client.name" label="Hosting Client" />
                      </el-table-column>
                    </el-table>
                  </template>
                </el-table-column>
                <el-table-column prop="structure.name" label="Structure" :min-width="120" />
                <el-table-column prop="rack.name" label="Rack" :min-width="120" />
                <el-table-column prop="minutes" label="Client Minutes" :min-width="88" />
                <el-table-column prop="infrastructure_minutes" label="Infrastructure Minutes" :min-width="88" />
                <el-table-column prop="total_affected_miners" label="Affected Miners" :min-width="104" />
              </el-table-column>
            </el-table>
          </template>
        </el-table-column>
        <el-table-column prop="site_name" label="Site" :min-width="196" />
        <el-table-column prop="for_hour_utc" label="Hour" :min-width="196" :formatter="formatHourInSelectedTz" />
        <el-table-column label="Hour Ending" :min-width="120">
          <template #default="scope">
            <p>HE {{ hourEndingFromHour(scope.row.for_hour_utc) }}</p>
          </template>
        </el-table-column>
        <el-table-column prop="downtime_type" label="Type" :min-width="196">
          <template #default="scope">
            {{ scope.row.downtime_type_category }} - {{ scope.row.downtime_type_name }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="flex justify-end"
        background
        :layout="DEFAULT_PAGINATION_LAYOUT"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        hide-on-single-page
      />
      <DowntimeDialog v-model="downtimeCreationDialogVisible" />
    </template>
  </ViewLayout>
</template>

<style>
.custom-header {
  .el-checkbox {
    display: flex;
    height: unset;
  }
}
</style>
